import { OrderSearchConditionEntity } from 'src/entities/OrderSearchCondition.entity';
import { PlanningConditions, PlanningSettingRequest } from 'src/entities/PlanningHistory.entity';
import { PlanningsGroupEntity } from 'src/entities/PlanningsGroup.entity';
import { SelectedCycle } from 'src/entities/SelectedCycle.entity';
import { TruckDisplayStatusVo } from 'src/vo/TruckDisplayStatusVo';

// usePlanningRequest.ts の deliveryEntities/truckEntities/driverEntities は画面表示用の重量級
// entity で、この関数が実際に参照するのは id/truckId/driverId/licensePlateValue/name だけ。
// 全フィールドを要求すると呼び出し側・テスト双方の fixture が肥大化するため、
// 参照するフィールドだけの最小形（構造的部分型）で受け取る。
export type PlanningSettingDeliveryLike = {
  id: number;
  truckId: number;
  driverId: number | null;
};

export type PlanningSettingTruckLike = {
  id: number;
  licensePlateValue: string;
};

export type PlanningSettingDriverLike = {
  id: number;
  name: string;
};

export type BuildPlanningSettingParams = {
  deliveryEntities: readonly PlanningSettingDeliveryLike[];
  selectedCycleIndexes: SelectedCycle[];
  truckDisplayStatus: TruckDisplayStatusVo;
  selectedGroupEntities: PlanningsGroupEntity[];
  allDeliverySelected: boolean;
  orderSearchKw: string;
  currentlyOrderSearching: boolean;
  orderSearchConditions: OrderSearchConditionEntity[];
  truckEntities: readonly PlanningSettingTruckLike[];
  driverEntities: readonly PlanningSettingDriverLike[];
  planningConditions: PlanningConditions;
};

/**
 * 自動配車リクエストに載せる planningSetting（deliveryConditions/orderConditions/planningConditions）を組み立てる。
 * V3Plans の inline 実装（hooks/usePlanningRequest.ts:123-154 相当）から抽出した純粋関数で、
 * 出力は既存実装と完全一致させている（本 PR では挙動を変えない特性テスト用の抽出）。
 */
export function buildPlanningSetting(params: BuildPlanningSettingParams): PlanningSettingRequest {
  const {
    deliveryEntities, selectedCycleIndexes, truckDisplayStatus, selectedGroupEntities,
    allDeliverySelected, orderSearchKw, currentlyOrderSearching, orderSearchConditions,
    truckEntities, driverEntities, planningConditions,
  } = params;

  const deliveryConditions = {
    truckDisplayStatus,
    selectedGroupIds: selectedGroupEntities.map((it) => it.id),
    selectedGroups: selectedGroupEntities,
    allDeliverySelected,
    selectedCycleIndexes,
    selectedDeliveries: selectedCycleIndexes.map((it) => {
      const { deliveryId } = it;
      const delivery = deliveryEntities.find((deli) => deli.id === deliveryId);
      if (!delivery) return null;

      const truck = truckEntities.find((t) => t.id === delivery.truckId);
      const driver = driverEntities.find((d) => d.id === delivery.driverId);
      return {
        deliveryId,
        truckId: truck?.id ?? null,
        licensePlateValue: truck?.licensePlateValue ?? null,
        driverId: driver?.id ?? null,
        driverName: driver?.name ?? null,
      };
    }),
  };

  const orderConditions = {
    orderSearchKw,
    orderSearchConditions: currentlyOrderSearching ? orderSearchConditions : [],
  };

  return {
    deliveryConditions,
    orderConditions,
    planningConditions,
  };
}
